import { pricingCategory } from "../enums/pricing.enum";
import dayjs from 'dayjs';
import { BookingInput, ValidationResult } from "../interfaces/booking.interface";
import { ChildAgeRanges } from "../interfaces/itinerary.interface";

export const childAgeLabel = (category: string, childAgeRanges?: ChildAgeRanges): string | null => {
  if (!category.includes("child")) return "12 years & above";
  const range = childAgeRanges?.[category];
  return range ? `${range.min_age}-${range.max_age} years old` : null;
};

//removes null values
export const nonNull = (arr, propName:string) => arr.map(a => a[propName]!).filter(a => a);

//creates a uniq and sorted values
export const uniqBy = (arr, propName:string) => {
  const values:string[] = nonNull(arr, propName)
  return [...new Set(values)].sort();
}

//parses and converts array to promise
export const storedArray$ = (array) => Promise.resolve(JSON.parse(array));

export const humanize = (str) => str.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, s => s.toUpperCase());

// price_type values carry an internal tier index (normal1, promo2, ...); strip it for display.
export const priceTypeLabel = (priceType: string) => humanize(priceType.replace(/\d/g, ""));

export const clean = (str) => str !=  null ? str.replace(/\s+/g, ' ') : "";

export const toTitleCase = (str: string): string => {
  return clean(str).toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
}

export const removeNullProperties = (params) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value != null && value != '') {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const groupedByPropName = (data, propName) => data.reduce((accumulator, currentObject) => {
  const key = currentObject[propName]; // The key to group by
  if (!accumulator[key]) {
    accumulator[key] = []; // Initialize an empty array for the key if it doesn't exist
  }
  accumulator[key].push(currentObject); // Add the current object to the corresponding group
  return accumulator;
}, {}); // Initialize the accumulator as an empty object

export const groupFareTiers = (items: { price_type?: string; price: number | string }[]) => {
  if (!items.some((item) => item.price_type)) return undefined;

  const byKey = new Map<string, { price_type: string; price: number; count: number; sum: number }>();
  items.forEach(({ price_type, price }) => {
    const key = `${price_type ?? ""}|${price}`;
    const entry = byKey.get(key) ?? { price_type: price_type || "", price: Number(price), count: 0, sum: 0 };
    entry.count++;
    entry.sum += Number(price);
    byKey.set(key, entry);
  });
  const tiers = Array.from(byKey.values()).sort((a, b) => a.price - b.price);
  return tiers;
};

export const summarizeFare = (data, type="series") => {
  const grouped = data.reduce((accumulator, currentItem) => {
    const { category, price, price_type } = currentItem;

    // If the category doesn't exist in the accumulator, initialize it
    if (!accumulator[category]) {
      accumulator[category] = { id: category, category: pricingCategory(type)[category] || humanize(category), sum: 0, count: 0, items: [] };
    }

    // Add the current item's price to the sum for its category
    accumulator[category].sum += Number(price);
    accumulator[category].count++;
    accumulator[category].items.push({ price_type, price });

    return accumulator;
  }, {});

  Object.values(grouped).forEach((entry: any) => {
    const tiers = groupFareTiers(entry.items);
    if (tiers) entry.tiers = tiers;
    delete entry.items;
  });

  return grouped;
};


export const totalPrice = (data) => data.reduce((accumulator, currentValue) => {
  return accumulator + (Number(currentValue.price) || 0)
}, 0);

// Parses "1st - 2nd Pax Promo" style display_price strings into a {start, end} pax range per tier row.
export const parsePricingTiers = (prices: any[]) => (prices || []).map((price) => {
  const rangeMatch = price.display_price?.match(/(\d+)(?:st|nd|rd|th)\s*-\s*(\d+)(?:st|nd|rd|th)/i);
  const singleMatch = !rangeMatch && price.display_price?.match(/(\d+)(?:st|nd|rd|th)/i);
  let start = 1, end = price.seats || Infinity;
  if (rangeMatch) {
    start = parseInt(rangeMatch[1]);
    end = parseInt(rangeMatch[2]);
  } else if (singleMatch) {
    start = end = parseInt(singleMatch[1]);
  }
  return { ...price, start, end };
});

export const getPricingTierRow = (pricingTiers: any[], passengerNumber: number) => {
  if (!pricingTiers || pricingTiers.length === 0) return undefined;
  const exact = pricingTiers.find((t) => passengerNumber >= t.start && passengerNumber <= t.end);
  if (exact) return exact;
  return [...pricingTiers].sort((a, b) => b.end - a.end)[0];
};

// Finds the tier price for the Nth (1-based, overall booking-order) passenger in a category.
export const getPriceTierForPassenger = (pricingTiers: any[], passengerNumber: number, category: string) => {
  const tier = getPricingTierRow(pricingTiers, passengerNumber);
  return tier ? Number(tier[category] || 0) : 0;
};

//currency format
export const currencyDisplay = (currency, value, fractionDigits?: number) => {
  if (currency === "MYR") {
    const digits = fractionDigits ?? 2
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    })
    return `RM ${formatter.format(value)}`
  }

  // Without fractionDigits the currency's own default applies (unchanged behaviour).
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    ...(fractionDigits === undefined
      ? {}
      : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })
  })
  return formatter.format(value)
}

// Fare-breakdown lines with a zero sum (e.g. infant when the price is 0 or not
// yet set) display "TBC" instead of a currency amount, matching the B2B price
// tables (see PriceDetails / B2C2BPriceDetails).
export const fareSumLabel = (currency, value) =>
  Number(value) === 0 ? "TBC" : currencyDisplay(currency, value)

export const parseCurrencyString = (formattedString: string, locale: string = 'en-US'): number => {
  const cleanedString = cleanCurrencyString(formattedString, locale);
  return parseFloat(cleanedString);
}

const cleanCurrencyString = (formattedString: string, locale: string = 'en-US'): string => {
  // Get locale-specific decimal and thousands separators
  const numberFormat = new Intl.NumberFormat(locale);
  const parts = numberFormat.formatToParts(1000.11); // Use a sample number to get parts
  const decimalSeparator = parts.find(part => part.type === 'decimal')?.value || '.';
  const thousandsSeparator = parts.find(part => part.type === 'group')?.value || ',';

  // Remove thousands separators and currency symbols
  let cleanedString = formattedString.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
  cleanedString = cleanedString.replace(/[^0-9\-\+.]/g, ''); // Remove non-numeric except sign and decimal

  // Replace locale-specific decimal separator with a standard dot for parsing
  cleanedString = cleanedString.replace(decimalSeparator, '.');

  return cleanedString;
}

export const formatDate = (date, format = "DD/MM/YYYY") => dayjs(new Date(date)).format(format);

export const isMalaysian = (nationality: string) => nationality === "Malaysia";

//moves the "Malaysia" entry to the front of a country options list, leaving the rest in order
export const withMalaysiaFirst = (countries: { value: string; label: string }[]) => {
  if (!Array.isArray(countries)) return [];
  const idx = countries.findIndex(c => c.value === "Malaysia");
  if (idx <= 0) return countries;
  return [countries[idx], ...countries.slice(0, idx), ...countries.slice(idx + 1)];
};

//temporary
const STAGING_DOMAINS = ["staging1.iceb2b.my", "staging2.iceb2b.my", "staging3.iceb2b.my", "staging4.iceb2b.my"];
const LOCAL_DOMAIN = "iceholidays2.localhost";
const LOCAL_DEV_DOMAIN = "iceb2b.dev";
const BRAND_DOMAINS = ["gd.my", "www.gd.my"];
const CUSTOMER_SIGNIN_URL = "/web/sign-in";
const PARTNER_SIGNIN_URL = "/web/partner-sign-in";

export const isB2C2B = typeof window !== "undefined" &&
  [...STAGING_DOMAINS, LOCAL_DOMAIN, LOCAL_DEV_DOMAIN, ...BRAND_DOMAINS].includes(window.location.hostname);
export const SIGNIN_URL = isB2C2B ? CUSTOMER_SIGNIN_URL : PARTNER_SIGNIN_URL;
//end

// A platform version qualifies for the redesigned landing page when its version
// is >= 2 (e.g. "2.1", "2.0" -> new layout; "1.0" -> legacy). Versions come from
// GET /b2c2b/api/v1/settings (b2c2b_desktop_version / b2c2b_mobile_version).
export const isNewLayoutVersion = (version?: string) => parseFloat(version ?? "") >= 2;

// Ground Tour tab rollout flag, staged independently of the v2 homepage itself
// (isNewLayoutVersion). Uses b2c2b_desktop_version regardless of breakpoint.
export const isGroundTourEnabled = (version?: string) => parseFloat(version ?? "") >= 2.2;

export const whatsappLink = (phone: string) => {
  return `https://wa.me/${phone}`;
}

export const emailLink = (email: string) => {
  return `mailto:${email}`;
}


const addOnKeys = ["addon_others", "compulsory_additional_fee"];
export const addons = (selectedTour, totalPax:number) => addOnKeys
  .map(addon_key => {
    const addonValue = Number(selectedTour[addon_key]) || 0;
    const addonPerPax = addonValue * totalPax;
    
    return {
      label: humanize(addon_key),
      value: addonPerPax,
      perPaxValue: addonValue,
      totalPax: totalPax
    };
  })
  .filter(addon => addon.value > 0);

export const CRUISE_CATEGORY = "CRUISE";

export const defaultPassengerInfos = (member: boolean = false) => ({member, infants: [], nationality: "Malaysia", category: null, surname: null, given_name: null, date_of_birth: null});

export const scrollIntoView = (id: string) => {
  return (
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  )
}

export function validateBooking(input: BookingInput, isCruise: boolean): ValidationResult {
  const { adult, child_twin, child_with_bed, child_no_bed, single_supplement, allowTripleRoom } = input;
  // 1. Sanity Checks
  const counts = [adult, child_twin, child_with_bed, child_no_bed, single_supplement];
  if (counts.some(val => val < 0)) {
    return { isValid: false, message: "Please enter a valid number of passengers." };
  }
  if (counts.reduce((sum, val) => sum + val, 0) === 0) {
    return { isValid: false, message: "Your journey starts with a selection! Please add at least one passenger." };
  }
 
  // 2. Base Logic Setup
  const basePax = adult + child_twin;
  let totalSharedRooms = 0;
  let configDetails = "";
 
  // 3. Branch A: Standard Twin Only (No Triples)
  if (!allowTripleRoom || isCruise) {
    if (child_with_bed > 0) {
      return {
        isValid: false, 
        message: "This tour only supports Twin rooms. Please move 'Child with Extra Bed' to 'Child Twin' or 'Single Supplement'."
      };
    }
 
    if (basePax > 0 && basePax % 2 !== 0) {
      return {
        isValid: false, 
        message: `Standard rooms are for 2 people. Since you have ${basePax} passengers, one person needs to be moved to a 'Single Room' or you can add one more traveler to share the room.`
      };
    }
 
    totalSharedRooms = basePax / 2;
    configDetails = `${totalSharedRooms} Twin Room(s)`;
 
  } else {
    // 4. Branch B: Triple Room Logic
    const requiredBaseForExtraBeds = child_with_bed * 2;
    if (basePax < requiredBaseForExtraBeds) {
      return {
        isValid: false,
        message: `To use an Extra Bed, a room must have 2 Adults/Children first. For ${child_with_bed} Extra Bed(s), you need at least ${requiredBaseForExtraBeds} regular passengers.`
      };
    }
 
    const remainingBasePax = basePax - requiredBaseForExtraBeds;
    const tripleRoomsWithExtraBed = child_with_bed;
 
    if (remainingBasePax === 1) {
      return {
        isValid: false,
        message: "Every traveler needs a roommate in a shared room. Please move the remaining person to a 'Single Room' or add one more person to your group."
      };
    }
 
    let twinRooms = 0;
    let pureTripleRooms = 0;
 
    if (remainingBasePax > 0) {
      if (remainingBasePax % 2 === 0) {
        twinRooms = remainingBasePax / 2;
      } else {
        pureTripleRooms = 1; 
        twinRooms = (remainingBasePax - 3) / 2;
      }
    }
 
    totalSharedRooms = tripleRoomsWithExtraBed + pureTripleRooms + twinRooms;
    const totalTriples = tripleRoomsWithExtraBed + pureTripleRooms;
    configDetails = `${twinRooms > 0 ? twinRooms + ' Twin(s)' : ''}${twinRooms > 0 && totalTriples > 0 ? ', ' : ''}${totalTriples > 0 ? totalTriples + ' Triple(s)' : ''}`;
  }
 
  // 5. Global Child No Bed Logic
  const grandTotalRooms = totalSharedRooms + single_supplement;
 
  if (child_no_bed > grandTotalRooms) {
    return {
      isValid: false,
      message: `We can only accommodate 1 'Child No Bed' per room. With ${grandTotalRooms} room(s) booked, please change the extra children to 'Child Twin' or 'Extra Bed'.`
    };
  }
 
  // 6. Success
  return {
    isValid: true,
    message: "Perfect! Your room configuration is valid.",
    totalRooms: grandTotalRooms,
    configuration: `${configDetails}${configDetails && single_supplement > 0 ? ', ' : ''}${single_supplement > 0 ? single_supplement + ' Single(s)' : ''}`
  };
}
